// Récupération de la clé maîtresse — Shamir 2-de-3.
//
// Le problème qu'elle résout est le prix à payer du chiffrement de bout en bout :
// la clé maîtresse n'est déballable que par une clé dérivée du mot de passe, et
// le serveur ne peut RIEN pour qui l'oublie. C'est la promesse du produit, et
// c'est aussi son piège — sans filet, un mot de passe oublié détruit un cabinet.
//
// Le filet : la clé est découpée en 3 parts dont **2 suffisent**.
//   • une pour l'avocat, qu'il exporte et met à l'abri (papier, coffre) ;
//   • deux pour des dépositaires, scellées à leur clé publique.
//
// Le serveur relaie des parts qu'il ne peut pas ouvrir, et n'en détient jamais
// assez pour reconstituer quoi que ce soit — même en réunissant tout ce qu'il a.

import { api } from '../api/client';
import {
  combineShares,
  deriveSubkey,
  loadCrypto,
  masterKeyFingerprint,
  proveReconstitution,
  sealShare,
  shareIntegrityHash,
  splitSecret,
  unsealShare,
  x25519PublicKey,
} from './index';

const toBase64 = (b: Uint8Array) => btoa(String.fromCharCode(...b));
const fromBase64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

/**
 * Clé privée X25519 de récupération, **dérivée** de la clé maîtresse.
 *
 * Un dépositaire n'a donc aucun secret supplémentaire à conserver — ce qui ne
 * ferait que déplacer le problème. Sa clé se recalcule à chaque connexion.
 */
const recoveryPrivateKey = (masterKey: Uint8Array) =>
  deriveSubkey(masterKey, 'recovery-x25519');

/** Publie ma clé publique de récupération : sans elle, nul ne peut me confier de part. */
export async function publishRecoveryKey(
  token: string,
  masterKey: Uint8Array,
): Promise<void> {
  await loadCrypto();
  const publicKey = x25519PublicKey(recoveryPrivateKey(masterKey));
  await api('/recovery/key', {
    method: 'PUT',
    token,
    body: { recoveryPublicKey: toBase64(publicKey) },
  });
}

// ---------------------------------------------------------------------------
// Mise en place (côté avocat)
// ---------------------------------------------------------------------------

export type Depositary = {
  id: string;
  email: string;
  userType: string;
  recoveryPublicKey: string;
};

export type ShareView = {
  id: string;
  shareIndex: number;
  depositaryType: string;
  depositaryEmail: string | null;
  status: string;
  acceptedAt: string | null;
};

export type Configuration = {
  id: string;
  threshold: number;
  totalShares: number;
  status: string;
  lastVerifiedAt: string | null;
  shares: ShareView[];
};

export function listDepositaries(token: string): Promise<Depositary[]> {
  return api<Depositary[]>('/recovery/depositaries', { token });
}

export function getConfiguration(token: string): Promise<Configuration | null> {
  return api<Configuration | null>('/recovery/configuration', { token });
}

/**
 * Découpe la clé maîtresse et distribue les parts.
 *
 * Renvoie **la part de l'avocat**, qui n'est écrite nulle part ailleurs : le
 * serveur ne la détient pas. À lui de l'exporter et de la mettre à l'abri —
 * s'il la perd, il lui faudra ses deux dépositaires.
 */
export async function setupRecovery(
  token: string,
  masterKey: Uint8Array,
  depositaries: [Depositary, Depositary],
): Promise<{ configuration: Configuration; selfShare: Uint8Array }> {
  await loadCrypto();

  // 2 parts sur 3 suffisent : l'avocat peut donc perdre son mot de passe (ses
  // deux dépositaires le sauvent), ou perdre un dépositaire (sa part + l'autre).
  const parts = splitSecret(masterKey, 2, 3) as Uint8Array[];
  const [selfShare, share2, share3] = parts;

  const seal = (share: Uint8Array, d: Depositary) => {
    const pub = fromBase64(d.recoveryPublicKey);
    return {
      sealedShareBlob: toBase64(sealShare(share, pub)),
      // L'empreinte ne révèle rien de la part, mais permet au dépositaire de
      // vérifier qu'il détient bien une part authentique.
      shareIntegrityHash: toBase64(shareIntegrityHash(share)),
      depositaryPubkeyFingerprint: toBase64(shareIntegrityHash(pub)),
    };
  };

  const configuration = await api<Configuration>('/recovery/configuration', {
    method: 'POST',
    token,
    body: {
      // Empreinte publique de la clé : elle ne la révèle pas, mais permettra au
      // serveur de vérifier une reconstitution sans jamais la connaître.
      masterKeyFingerprint: toBase64(masterKeyFingerprint(masterKey)),
      shares: [
        {
          shareIndex: 1,
          depositaryType: 'self',
          // Pas de `sealedShareBlob` : le serveur ne détient PAS la part de
          // l'avocat. Une copie scellée par son mot de passe serait morte
          // justement le jour où il l'oublie.
          shareIntegrityHash: toBase64(shareIntegrityHash(selfShare!)),
          depositaryPubkeyFingerprint: toBase64(
            shareIntegrityHash(x25519PublicKey(recoveryPrivateKey(masterKey))),
          ),
        },
        {
          shareIndex: 2,
          depositaryType: depositaries[0].userType.startsWith('avocat')
            ? 'associate'
            : 'other',
          depositaryUserId: depositaries[0].id,
          ...seal(share2!, depositaries[0]),
        },
        {
          shareIndex: 3,
          depositaryType: depositaries[1].userType.startsWith('avocat')
            ? 'associate'
            : 'other',
          depositaryUserId: depositaries[1].id,
          ...seal(share3!, depositaries[1]),
        },
      ],
    },
  });

  // Preuve de reconstitution, faite ICI et pas ailleurs : c'est le seul moment
  // où l'on détient encore deux parts en clair. Après, les parts 2 et 3 seront
  // scellées à leurs dépositaires — l'avocat ne pourra plus les ouvrir.
  //
  // On recombine réellement S₁ et S₂ (ce qui VÉRIFIE le découpage), et on signe
  // le défi du serveur avec la clé obtenue. Le serveur contrôle la signature
  // contre l'empreinte publique qu'il détient : il acquiert la certitude que les
  // parts reconstituent bien LA bonne clé, sans jamais l'apprendre. Une erreur de
  // distribution se verrait donc maintenant — pas le jour d'une récupération.
  await prouver(token, combineShares([selfShare!, share2!]));

  return {
    configuration: await getConfiguration(token).then((c) => c ?? configuration),
    selfShare: selfShare!,
  };
}

async function prouver(token: string, reconstituee: Uint8Array): Promise<void> {
  const { challenge } = await api<{ challenge: string }>('/recovery/challenge', {
    method: 'POST',
    token,
  });
  const proof = proveReconstitution(reconstituee, fromBase64(challenge));
  await api('/recovery/prove', {
    method: 'POST',
    token,
    body: { challenge, proof: toBase64(proof) },
  });
}

// ---------------------------------------------------------------------------
// Côté dépositaire
// ---------------------------------------------------------------------------

export type HeldShare = {
  id: string;
  ownerEmail: string;
  sealedShareBlob: string;
  shareIntegrityHash: string;
  status: string;
};

export function listHeldShares(token: string): Promise<HeldShare[]> {
  return api<HeldShare[]>('/recovery/shares', { token });
}

/**
 * Accepte la garde d'une part — **après l'avoir descellée et vérifiée**.
 *
 * Accepter sans pouvoir l'ouvrir serait une garde fictive : on ne s'en
 * apercevrait que le jour d'une récupération, c'est-à-dire trop tard. On
 * descelle donc réellement, et on contrôle l'empreinte.
 */
export async function acceptShare(
  token: string,
  masterKey: Uint8Array,
  share: HeldShare,
): Promise<void> {
  await loadCrypto();

  const descellee = unsealShare(
    fromBase64(share.sealedShareBlob),
    recoveryPrivateKey(masterKey),
  );

  const attendu = fromBase64(share.shareIntegrityHash);
  const obtenu = shareIntegrityHash(descellee);
  if (toBase64(obtenu) !== toBase64(attendu)) {
    throw new Error(
      "La part ne correspond pas à son empreinte : ne l'accepte pas.",
    );
  }

  await api(`/recovery/shares/${share.id}/accept`, { method: 'POST', token });
}

/** Descelle une part dont je suis dépositaire (pour une récupération à venir). */
export async function unsealHeldShare(
  masterKey: Uint8Array,
  share: HeldShare,
): Promise<Uint8Array> {
  await loadCrypto();
  return unsealShare(
    fromBase64(share.sealedShareBlob),
    recoveryPrivateKey(masterKey),
  );
}
